export const preloadKey = row => row.source_key || row.id;
const sharedToken = row => JSON.stringify(Object.entries(row.shared_versions || {}).sort(([a], [b]) => a.localeCompare(b)));

// Capturing the first photo can change the day token, never another owner's data.
export function compatiblePreloadBatchRow(before, next) {
  return next && before.id === next.id && before.version === next.version
    && before.status === next.status && before.source_version === next.source_version
    && sharedToken(before) === sharedToken(next)
    && before.cycle_id === next.cycle_id && next.day_editable !== false;
}

// services : soirées des employés (PreloadEvening) ; leurs préchargements (members) se valident ensemble, en un appel.
export async function runPreloadBatch(entries, { validate, refresh, report, services = [] }) {
  const pending = entries.filter(entry => entry.row.status === "draft" && (validate || entry.dirty));
  const evenings = services.filter(service => service.row.status === "draft"
    && (service.dirty || (validate && service.members.some(key => pending.some(entry => entry.key === key)))));
  for (const entry of [...pending, ...evenings]) {
    const error = validate ? entry.validationError : entry.saveError;
    if (error) throw new Error(`${entry.label} : ${error}`);
  }
  const current = new Map(pending.map(entry => [entry.key, entry.baseline]));
  for (const service of evenings) current.set(service.key, service.baseline);
  // Une soirée s'enregistre avec le jeton de sa journée d'attache, le plus récent : la première sauvegarde prend la photo du Dispatch et le change.
  let lines = [...current.values()];
  const token = service => lines.find(row => row?.day_version && row.delivery_date === service.day)?.day_version || service.dayVersion;
  // Préchargements que leur soirée vient de valider : leur nouvelle version est attendue.
  const settled = new Set();
  const invoke = async (entry, action, ...args) => {
    try { return await entry[action](current.get(entry.key), ...args); }
    catch (error) { error.message = `${entry.label} : ${error.message}`; throw error; }
  };
  const reload = async () => {
    const data = await refresh();
    if (!data?.days) throw new Error("Actualisation impossible. Les saisies restantes sont conservées.");
    const rows = data.days.flatMap(day => [...day.preloads, ...(day.evening_preloads || [])]);
    lines = rows;
    for (const entry of pending) {
      const before = current.get(entry.key);
      const next = rows.find(row => preloadKey(row) === entry.key || (before.id && row.id === before.id));
      if (settled.has(entry.key) && next) { current.set(entry.key, next); continue; }
      if (!compatiblePreloadBatchRow(before, next)) throw new Error(`${entry.label} a changé. Les saisies restantes sont conservées.`);
      current.set(entry.key, next);
    }
    const saved = data.days.flatMap(day => day.preload_services || []);
    for (const service of evenings) {
      current.set(service.key, saved.find(row => row.employee_id === service.employeeId && row.execution_date === service.executionDate) || null);
    }
  };
  for (const entry of pending) {
    if (!entry.dirty && entry.row.id) continue;
    current.set(entry.key, await invoke(entry, "save", validate));
    report(entry, "saved");
    await reload();
  }
  for (const service of evenings) {
    if (!service.dirty && current.get(service.key)) continue;
    current.set(service.key, await invoke(service, "save", token(service)));
    report(service, "saved");
    await reload();
  }
  if (!validate) return;
  const grouped = new Set(evenings.flatMap(service => service.members));
  for (const entry of pending.filter(entry => !grouped.has(entry.key))) {
    current.set(entry.key, await invoke(entry, "validate", validate));
    report(entry, "validated");
    await reload();
  }
  for (const service of evenings) {
    await invoke(service, "validate", service.members.map(key => current.get(key) || entries.find(entry => entry.key === key)?.baseline), token(service));
    service.members.forEach(key => settled.add(key));
    report(service, "validated");
    await reload();
  }
}
